<template>
  <div>
    <b-modal
      modal-class="component-modal-preview-receipt"
      id="PaymentReceiptPreviewModal"
      ref="PaymentReceiptPreviewModal"
      size="xl fluid"
      centered
      no-close-on-backdrop
      no-close-on-esc
      @hide="closeReceipt"
      @hidden="defaultValue"
      :hide-footer="(mode == 'cancel' || displayOnly) && !cancelOnlyMode"
    >
      <template #modal-title>
        พิมพ์ใบเสร็จรับเงิน
        <i id="receipt-setting-gear" class="fas fa-gear"></i>
      </template>
      <b-popover
        target="receipt-setting-gear"
        triggers="hover"
        placement="top"
        title="แสดงคอลัมน์"
      >
        <b-checkbox
          v-for="field in displayFields"
          :key="field.key"
          v-model="field.visible"
          switch
          @change="saveSetting"
        >
          {{ field.label }}
        </b-checkbox>
      </b-popover>
      <b-tabs v-if="!cancelOnlyMode" v-model="tabIndex" card justified>
        <b-tab title="ภาษาไทย">
          <ReceiptPreview
            :ref="tabRefs[0]"
            :lang="locale[0]"
            :afterPrint="afterPrint"
            :createReceiptSuccess="createReceiptSuccess"
            containerTarget="#print"
            :mode="mode"
            :receiptData="receipt"
            :patientData="patient"
            :branchData="branchData"
            :displayKeys="displayKeys"
          />
        </b-tab>
        <b-tab title="English">
          <ReceiptPreview
            :ref="tabRefs[1]"
            :lang="locale[1]"
            :afterPrint="afterPrint"
            :createReceiptSuccess="createReceiptSuccess"
            containerTarget="#print"
            :mode="mode"
            :receiptData="receipt"
            :patientData="patient"
            :branchData="branchData"
            :displayKeys="displayKeys"
          />
        </b-tab>
      </b-tabs>
      <div v-else class="text-center py-5">
        <i class="fas fa-exclamation-triangle fa-3x text-warning mb-3 d-block"></i>
        <h5>ไม่สามารถแสดงข้อมูลใบเสร็จได้</h5>
      </div>
      <template #modal-footer>
        <div class="w-100">
          <div class="row">
            <div v-if="mode === 'original'" class="col-12 d-flex">
              <b-button
                variant="primary"
                class="col-3 mx-auto"
                @click="issueOriginalReceipt"
              >
                พิมพ์ใบเสร็จ
              </b-button>
            </div>

            <div v-else class="col-12 d-flex">
              <b-button
                v-if="showCancelReceipt"
                variant="danger"
                class="col-3 mx-auto"
                data-testid="receipt-cancel-button"
                @click="cancelReceipt"
              >
                ยกเลิกใบเสร็จ
              </b-button>
              <b-button
                v-if="!cancelOnlyMode"
                variant="primary"
                class="col-3 mx-auto"
                @click="debouncedPrintReceipt"
              >
                <img
                  v-if="getNhsoBookingNo && mode === 'create' && !isSaved"
                  class="mr-1"
                  style="width: auto; height: 24px"
                  src="@/assets/img//NHSO_logo.png"
                />
                <img
                  v-if="isPaotang"
                  class="mr-1"
                  style="width: auto; height: 24px"
                  src="@/assets/img/Paotang-small.png"
                />
                {{ printButtonText }}
              </b-button>
            </div>
          </div>
        </div>
      </template>
    </b-modal>
    <RefNoModal
      ref="refModal"
      :error-message="transactionError && transactionError.message"
      @ref-confirm="handleRefConfirm"
      @ref-cancel="handleRefCancel"
      @ref-retry="handleRefRetry"
    />
    <VerifyModal ref="verifyModal" :afterCancel="afterCancel" />
    <Dialog ref="Dialog"></Dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import axios from "axios";
import bridgeService, { EdcError } from "@/services/HardwareBridgeService";

import VerifyModal from "@/components/modal/VerifyCancelReceipt";
import ReceiptPreview from "@/components/ReceiptPreview";
import Dialog from "@/components/modal/Dialog";
import isEmpty from "lodash/isEmpty";
import { eventBus } from "@/main";
import debounce from "@/utils/debounce";
import RefNoModal from "@/components/modal/RefNo";

export default {
  name: "PaymentReceiptPreviewModal",
  components: {
    VerifyModal,
    ReceiptPreview,
    Dialog,
    RefNoModal,
  },
  data() {
    return {
      tabIndex: 0,
      tabRefs: ["ReceiptTh", "ReceiptEn"],
      locale: ["th", "en"],
      receipt: {},
      patient: {},
      branchData: null, // Store branch data
      mode: "create",
      isSaved: false,
      showCancelReceipt: false,
      displayOnly: false,
      cancelOnlyMode: false,
      cancelOnlyReceiptId: null,

      displayFields: [
        {
          key: "doctor",
          label: "แพทย์",
          visible: true,
        },
        {
          key: "quantity",
          label: "จำนวน",
          visible: true,
        },
        {
          key: "price",
          label: "ราคา",
          visible: true,
          formatter: this.$utils.formatPrice,
        },
        {
          key: "finalDiscount",
          label: "ส่วนลด",
          visible: true,
        },
      ],
      displayKeys: ["doctor", "quantity", "price", "finalDiscount"],
      debouncedPrintReceipt: null,
      dcPayment: {},

      transactionError: null,
      refModalResolve: null,

      // EDC HTTP API Configuration
      edcApiBaseUrl: "http://localhost:8000",
      statusCheckTimeoutMinutes: 5, // minutes
      statusCheckInterval: 2000, // 2 seconds

      // Polling control
      shouldContinuePolling: true,
    };
  },
  computed: {
    ...mapGetters({
      getNhsoBookingNo: "moduleAppointment/getNhsoBookingNo",
    }),
    maxStatusCheckAttempts() {
      return Math.ceil(
        (this.statusCheckTimeoutMinutes * 60 * 1000) / this.statusCheckInterval
      );
    },
    printButtonText() {
      if (this.mode == "create" && !this.isSaved) {
        if (this.getNhsoBookingNo) return "ส่งข้อมูล สปสช และบันทึกใบเสร็จ";
        if (this.isPaotang) return "ชำระด้วยเป๋าตัง";
        return "บันทึกและพิมพ์ใบเสร็จ";
      }
      return "พิมพ์ใบเสร็จ";
    },
    isPaotang() {
      return this.receipt?.displayAmount.filter(i => i.cateId === 6).length > 0;
    },
  },
  watch: {},
  methods: {
    ...mapActions({
      fetchPatientById: "modulePatient/fetchPatientById",
      fetchReceiptById: "moduleReceipt/fetchReceiptById",
      issueReprint: "moduleReceipt/issueReprint",
    }),

    fetchAndShow({ id, patientId, cancel }) {
      this.isLoading = true;

      // Only fetch the receipt - it already contains patient and branch data
      this.fetchReceiptById({
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
        id,
      })
        .then(receiptRes => {
          if (receiptRes.status == 200 && receiptRes.data.id) {
            const receiptData = receiptRes.data;

            // Store branch data directly from receipt
            if (receiptData.branch) {
              this.branchData = receiptData.branch;
            }

            // Use patient data directly from the receipt
            const patientData = receiptData.patient || {};

            // Show the receipt with all data from single response
            if (patientData.id) {
              this.show(
                receiptData,
                patientData,
                cancel ? "cancel" : "copy",
                this.isManager || this.isHeadCounter
              );
            } else {
              eventBus.$emit("alertSwal", {
                title: "ระบบขัดข้อง",
                message: "ไม่พบข้อมูลผู้ป่วย",
                icon: "error",
              });
            }
          } else {
            eventBus.$emit("alertSwal", {
              title: "ระบบขัดข้อง",
              message: "ไม่สามารถแสดงข้อมูลใบเสร็จได้",
              icon: "error",
            });
          }
        })
        .catch(err => {
          console.error("fetchAndShow error:", err);
          eventBus.$emit("alertSwal", {
            title: "ระบบขัดข้อง",
            message: "ไม่สามารถแสดงข้อมูลใบเสร็จได้",
            icon: "error",
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    show(receiptData, patientData, mode, showCancelReceipt, displayOnly) {
      this.receipt = receiptData;
      this.patient = patientData;
      this.mode = mode || "create";
      this.showCancelReceipt = showCancelReceipt && this.mode == "copy";
      this.displayOnly = displayOnly || false;
      this.loadSetting();

      if (this.receipt?.receiptPayment) {
        const dcPayments = this.receipt.receiptPayment.filter(
          i => i.dcPaymentType
        );

        if (dcPayments.length > 1) {
          throw new Error("Multiple DC Payments found");
        } else {
          this.dcPayment = dcPayments[0];
        }
      }

      this.$refs["PaymentReceiptPreviewModal"].show();
    },

    showCancelOnly(receiptId) {
      this.cancelOnlyMode = true;
      this.cancelOnlyReceiptId = receiptId;
      this.mode = "copy";
      this.showCancelReceipt = true;
      this.displayOnly = false;
      this.receipt = {};
      this.patient = {};
      this.$refs["PaymentReceiptPreviewModal"].show();
    },

    showOriginalReceipt(receiptData, patientData, mode) {
      this.receipt = receiptData;
      this.patient = patientData;
      this.mode = "original";
      this.loadSetting();
      this.$refs["PaymentReceiptPreviewModal"].show();
    },
    async issueOriginalReceipt() {
      try {
        this.$refs[this.tabRefs[this.tabIndex]].print();
        await this.issueReprint({
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
          data: {
            id: this.receipt.id,
          },
        });
      } catch (err) {
        console.error(err);
      }
    },

    hide() {
      this.$refs["PaymentReceiptPreviewModal"].hide();
    },
    async printReceipt() {
      if (!this.dcPayment?.receiptPaymentId) {
        this.printReceiptDocument();

        // console.error('Invalid payment data');
        return;
      }
      eventBus.$emit("setEdcTimeout", this.statusCheckTimeoutMinutes);
      eventBus.$emit("edcProcessingStatus", true);
      let error;
      try {
        const receiptNo = await this.createPendingReceipt();
        const transactionResponse = await this.processTransaction(receiptNo);

        await this.updateDentCloudReceiptPayment(transactionResponse);

        this.printReceiptDocument();
      } catch (err) {
        error = err;
        console.error("Transaction failed:", error);
      } finally {
        eventBus.$emit("edcProcessingStatus", false);
        if (error) {
          await this.handleTransactionError(error);
        }
      }
    },
    async createPendingReceipt() {
      // TODO: Implement API call to create pending receipt
      return "REC0001";
    },
    async processTransaction(receiptNo) {
      // Try WS Hardware Bridge first
      try {
        return await this.processViaBridge(receiptNo);
      } catch (bridgeErr) {
        // Only fall back to HTTP if bridge connection failed
        if (bridgeErr.code === "EDC_NOT_CONNECTED") {
          console.warn("Hardware Bridge unavailable, falling back to HTTP API:", bridgeErr.message);
        } else {
          // EDC error (declined, cancelled, timeout) — don't retry via HTTP
          throw bridgeErr;
        }
      }

      // Fallback: existing HTTP API flow
      try {
        const startPaymentResponse = await this.startPaymentTransaction(
          receiptNo
        );

        if (!startPaymentResponse.transaction_id) {
          throw new Error("Failed to start payment transaction");
        }

        const transactionResponse = await this.pollTransactionStatus(
          startPaymentResponse.transaction_id,
          this.maxStatusCheckAttempts,
          this.statusCheckInterval
        );

        return transactionResponse;
      } catch (error) {
        console.error("Payment transaction error:", error);
        throw error;
      }
    },

    async processViaBridge(receiptNo) {
      try {
        await bridgeService.connect();
      } catch (err) {
        throw new EdcError({ code: "EDC_NOT_CONNECTED", message: err.message, recoverable: true });
      }

      const action = this.dcPayment.dcPaymentType === "qr" ? "qr-sale" : "sale";

      const message = {
        type: "edc",
        action,
        data: {
          amount: this.dcPayment.amount,
          ref1: this.receipt?.treatmentId || "",
          ref2: this.patient?.dn || "",
          vatRefund: 0,
        },
      };

      console.log("[EDC] Sending to bridge:", { action, amount: this.dcPayment.amount, dcPayment: this.dcPayment });

      return new Promise((resolve, reject) => {
        let unsub = null;

        const ackTimeout = setTimeout(() => {
          if (unsub) unsub();
          reject(new EdcError({ code: "EDC_NO_ACK", message: "ACK not received within 5 seconds", recoverable: true }));
        }, 5000);

        let responseTimeout = null;

        unsub = bridgeService.send(message, (response) => {
          // Handle ACK
          if (response.event === "ack") {
            clearTimeout(ackTimeout);
            responseTimeout = setTimeout(() => {
              if (unsub) unsub();
              reject(new EdcError({ code: "EDC_RESPONSE_TIMEOUT", message: "EDC response timeout", recoverable: true }));
            }, 60000);
            return;
          }

          // Handle final response
          clearTimeout(responseTimeout);
          if (unsub) unsub();

          if (response.event === "success") {
            resolve({
              success: true,
              transactionId: response.data.invoiceNo || "",
              referenceNo: response.data.approvalCode || "",
              amount: response.data.amount || this.dcPayment.amount,
              status: "completed",
              responseCode: response.data.responseCode,
              responseMessage: response.data.responseMessage,
              rawResponse: response.data,
            });
          } else if (response.event === "error") {
            reject(new EdcError(response.error || { code: "EDC_ERROR", message: "EDC Error", recoverable: false }));
          } else {
            reject(new EdcError({ code: "EDC_ERROR", message: `Unexpected response: ${JSON.stringify(response)}`, recoverable: false }));
          }
        });
      });
    },

    async startPaymentTransaction(receiptNo) {
      try {
        // Map dcPaymentType to API type (assuming 0=credit card, 1=QR code)
        const paymentType = this.dcPayment.dcPaymentType === "qr" ? 1 : 0;

        const response = await axios.post(`${this.edcApiBaseUrl}/sales-async`, {
          type: paymentType,
          amount: this.dcPayment.amount,
          // invoice: receiptNo, // Include receipt number as invoice reference
        }, {
          timeout: 10000 // 10 seconds timeout
        });

        const data = response.data;

        return data;
      } catch (error) {
        console.error("Start payment error:", error);
        
        // For 503 errors, pass the detail message for specific error handling
        if (error.response?.status === 503) {
          const errorDetail = error.response.data?.detail;
          throw new Error(errorDetail || `Failed to start payment: ${error.message}`);
        }
        
        throw new Error(`Failed to start payment: ${error.message}`);
      }
    },

    async pollTransactionStatus(
      transactionId,
      maxAttempts = 60,
      pollInterval = 2000
    ) {
      let attempts = 0;
      this.shouldContinuePolling = true;

      while (attempts < maxAttempts && this.shouldContinuePolling) {
        try {
          const response = await axios.get(
            `${this.edcApiBaseUrl}/check-status/${transactionId}`
          );

          const statusData = response.data;

          // Check if transaction is completed
          if (
            statusData.status === "completed" ||
            statusData.status === "success"
          ) {
            return {
              success: true,
              transactionId: statusData.tx_id,
              referenceNo: statusData.approval,
              amount: statusData.amount,
              status: statusData.status,
              responseCode: statusData.resp_code,
              responseMessage: statusData.resp_msg,
              rawResponse: statusData,
            };
          }

          // Check if transaction failed - these are terminal states, stop polling immediately
          if (statusData.status === "error") {
            throw new Error("ERROR");
          }

          if (statusData.status === "fail") {
            throw new Error(statusData.resp_msg || "Transaction failed");
          }

          if (statusData.status === "timeout") {
            throw new Error("TIMEOUT");
          }

          // If still processing, wait and try again
          if (statusData.status === "processing") {
            attempts++;
            if (attempts < maxAttempts && this.shouldContinuePolling) {
              await this.delay(pollInterval);
              continue;
            } else {
              throw new Error(
                "Transaction timeout - maximum polling attempts reached"
              );
            }
          }

          // Unknown status
          throw new Error(`Unknown transaction status: ${statusData.status}`);
        } catch (error) {
          console.error(
            `Status check error on attempt ${attempts + 1}:`,
            error
          );

          // Check if this is an HTTP response error (4xx, 5xx status codes)
          if (error.response && error.response.status >= 400) {
            console.error(
              `HTTP error ${error.response.status}: ${error.response.statusText}`
            );
            throw new Error(
              `EDC API error: ${error.response.status} ${error.response.statusText}`
            );
          }

          // Business logic errors - stop polling immediately
          if (
            error.message === "ERROR" ||
            error.message === "TIMEOUT" ||
            error.message.includes("Transaction failed") ||
            error.message.includes("Unknown transaction status") ||
            error.message.includes("Transaction timeout") ||
            error.message.includes("EDC API error")
          ) {
            throw error;
          }

          // Network/connection errors - continue polling with limits
          const isNetworkError =
            error.code === "ECONNREFUSED" ||
            error.code === "ENOTFOUND" ||
            error.code === "ETIMEDOUT" ||
            error.message.includes("Network Error") ||
            error.message.includes("timeout");

          if (
            isNetworkError &&
            attempts < maxAttempts - 1 &&
            this.shouldContinuePolling
          ) {
            console.warn(
              `Network error, retrying in ${pollInterval}ms... (attempt ${
                attempts + 1
              }/${maxAttempts})`
            );
            attempts++;
            await this.delay(pollInterval);
            continue;
          }

          // Max attempts reached or unknown error - stop polling
          throw error;
        }
      }

      if (!this.shouldContinuePolling) {
        throw new Error("Transaction polling cancelled");
      }

      throw new Error("Transaction polling timeout");
    },

    delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },

    async updateDentCloudReceiptPayment(transactionResponse = {}) {
      try {
        // Create new receiptPayment array
        const updatedPayments = [...this.receipt.receiptPayment];

        // Find the payment to update
        const paymentIndex = updatedPayments.findIndex(
          payment => payment.id === this.dcPayment.id
        );

        if (paymentIndex !== -1) {
          // Update the specific payment
          updatedPayments[paymentIndex] = {
            ...updatedPayments[paymentIndex],
            transactionLog: JSON.stringify(transactionResponse),
            referenceId: transactionResponse?.referenceNo,
          };

          // Use $set to update both the receipt and its nested receiptPayment
          this.$set(this.receipt, "receiptPayment", updatedPayments);
        }
      } catch (err) {
        console.error("Error updating DentCloud receipt payment:", err);
        throw err;
      }
    },

    printReceiptDocument() {
      this.$refs[this.tabRefs[this.tabIndex]].print(this.isPaotang);
    },

    async showConfirmPrintReceiptDialog(error) {
      if (error?.message === "TXN CANCEL" ||
          error?.code === "EDC_CANCELLED" ||
          error?.message === "Transaction polling cancelled") {
        return { isConfirmed: false };
      }

      this.transactionError = error;

      return new Promise(resolve => {
        this.refModalResolve = resolve;
        this.$refs.refModal.show();
      });
    },

    handleRefConfirm(data) {
      if (this.refModalResolve) {
        this.refModalResolve({
          isConfirmed: true,
          referenceId: data.referenceId,
        });
        this.refModalResolve = null;
      }
    },

    handleRefCancel() {
      if (this.refModalResolve) {
        this.refModalResolve({ isConfirmed: false });
        this.refModalResolve = null;
      }
    },

    handleRefRetry() {
      if (this.refModalResolve) {
        this.refModalResolve({ isConfirmed: false, retry: true });
        this.refModalResolve = null;
      }
      // Retry the payment operation
      this.printReceipt();
    },

    async handleTransactionError(error) {
      const result = await this.showConfirmPrintReceiptDialog(error);
      if (result.isConfirmed && result.referenceId) {
        await this.updateDentCloudReceiptPayment({
          referenceNo: result.referenceId,
        });
        this.printReceiptDocument();
      }
    },
    createReceiptSuccess() {
      this.isSaved = true;
    },
    cancelReceipt() {
      if (this.cancelOnlyMode && this.cancelOnlyReceiptId) {
        this.$refs.verifyModal.show(this.cancelOnlyReceiptId);
      } else if (this.mode == "copy" && !isEmpty(this.receipt)) {
        this.$refs.verifyModal.show(this.receipt.id);
      }
    },
    closeReceipt() {
      if (this.isSaved) {
        this.$emit("afterClose");
      }
    },
    afterPrint() {
      this.$emit("afterPrint");
      this.hide();
    },
    afterCancel() {
      this.hide();
      this.$emit("afterCancel");
      eventBus.$emit("afterCancelReceipt");
    },
    getKeys() {
      return this.displayFields.filter(i => i.visible).map(i => i.key);
    },
    async saveSetting() {
      await this.$nextTick();
      this.displayKeys = this.getKeys();
      localStorage.setItem(
        "receiptDisplayKeys",
        JSON.stringify(this.displayKeys)
      );
    },
    async loadSetting() {
      this.displayKeys = JSON.parse(
        localStorage.getItem("receiptDisplayKeys")
      ) || ["doctor", "quantity", "price", "finalDiscount"];
      this.displayFields.forEach(i => {
        if (this.displayKeys.includes(i.key)) return;
        i.visible = false;
      });
    },
    abortPolling() {
      this.shouldContinuePolling = false;
    },

    defaultValue() {
      this.receipt = {};
      this.patient = {};
      this.dcPayment = {};
      this.mode = "create";
      this.isSaved = false;
      this.showCancelReceipt = false;
      this.displayOnly = false;
      this.cancelOnlyMode = false;
      this.cancelOnlyReceiptId = null;
      this.shouldContinuePolling = true;
    },
  },
  mounted() {
    this.debouncedPrintReceipt = debounce(this.printReceipt);

    // Listen for LoadingEdc events to abort polling
    eventBus.$on("abortPolling", this.abortPolling);
    eventBus.$on("transactionTimeout", this.abortPolling);
    eventBus.$on("cancelTransaction", this.abortPolling);
  },

  beforeDestroy() {
    // Clean up event listeners
    eventBus.$off("abortPolling", this.abortPolling);
    eventBus.$off("transactionTimeout", this.abortPolling);
    eventBus.$off("cancelTransaction", this.abortPolling);
  },
};
</script>

<style>
.component-modal-preview-receipt .modal-body {
  padding: 0;
}
</style>